import React from "react";
import { Link, useLocation } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";
import DynamicTitle from "./DynamicTitle";
import Accordion from "./Accordion";
import WaveDivider from "./WaveDivider";

function AboutEMDR() {
  const location = useLocation();
  return (
    <div className="flex flex-col min-h-screen">
      <DynamicTitle>The Science</DynamicTitle>
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-emdr-hero bg-cover bg-center text-white relative pb-0">
          <div className="max-w-screen-xl mx-auto px-4 text-center py-20">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              The Science Behind Bilateral Stimulation
            </h1>
            <p className="text-lg sm:text-xl mb-8">
              Explore the research and evidence that supports our approach.
            </p>
          </div>
          <div className="relative">
            <WaveDivider topColor="transparent" bottomColor="background" />
          </div>
        </section>

        {/* Research Foundation */}
        <section className="pt-4 pb-12 bg-background" style={{ marginTop: "-1px" }}>
          <div className="max-w-screen-md mx-auto px-4">
            <h2 className="text-2xl font-bold mb-4 text-center text-text">
              Research-Inspired Approach
            </h2>
            <p className="text-base mb-4 text-textSecondary">
              Our sessions are inspired by decades of research into bilateral stimulation techniques,
              including studies on Eye Movement Desensitization and Reprocessing (EMDR).
              This research has shown that bilateral stimulation can help process difficult
              experiences and promote mental wellness.
            </p>
            <p className="text-base mb-4 text-textSecondary">
              While our sessions are not therapy and cannot replace professional mental health
              care, they are designed to provide a supportive experience that may complement
              your overall wellness journey.
            </p>
            <p className="text-base text-textSecondary">
              <strong>Important:</strong> Our sessions are not intended to diagnose, treat,
              cure, or prevent any mental health condition. Always consult with qualified
              mental health professionals for proper care.
            </p>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="surfaceTint" />

        {/* How Our Sessions Work */}
        <section className="pt-8 pb-12 bg-surfaceTint">
          <div className="max-w-screen-xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-8 text-center text-text">
              How Our Sessions Work
            </h2>
            <div className="flex flex-wrap -mx-4">
              <div className="w-full md:w-1/2 px-4 mb-8">
                <h3 className="text-xl font-semibold mb-4 text-text">
                  Our Session Process
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="text-2xl font-bold text-accent mr-3">1</div>
                    <div>
                      <h4 className="font-semibold text-text">Mood Assessment</h4>
                      <p className="text-textSecondary text-sm">Track your emotional state before and after sessions</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="text-2xl font-bold text-accent mr-3">2</div>
                    <div>
                      <h4 className="font-semibold text-text">Guided Focus</h4>
                      <p className="text-textSecondary text-sm">Follow gentle prompts while maintaining awareness</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="text-2xl font-bold text-accent mr-3">3</div>
                    <div>
                      <h4 className="font-semibold text-text">Bilateral Stimulation</h4>
                      <p className="text-textSecondary text-sm">Visual and auditory cues to support processing</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="text-2xl font-bold text-accent mr-3">4</div>
                    <div>
                      <h4 className="font-semibold text-text">Integration</h4>
                      <p className="text-textSecondary text-sm">Gentle closure and reflection on your experience</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2 px-4 mb-8">
                <img
                  src="/images/how-emdr-works.webp"
                  alt="Session Process"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>
            <p className="text-base text-textSecondary text-center mt-6">
              Our sessions use bilateral stimulation techniques inspired by research,
              helping you process experiences in a supportive, self-directed way.
            </p>
          </div>
        </section>

        <WaveDivider topColor="surfaceTint" bottomColor="background" />

        {/* Research Benefits */}
        <section className="pt-8 pb-12 bg-background">
          <div className="max-w-screen-xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-8 text-text">
              What Research Shows
            </h2>
            <div className="flex flex-wrap -mx-4">
              <div className="w-full md:w-1/3 px-4 mb-8">
                <div className="p-6 bg-white rounded-lg border border-tint">
                  <img
                    src="/images/effective-treatment.webp"
                    alt="Research Support"
                    className="mx-auto mb-4 w-16 h-16"
                  />
                  <h3 className="text-xl font-semibold mb-2 text-text">
                    Research-Backed Techniques
                  </h3>
                  <p className="text-base text-textSecondary">
                    Studies show bilateral stimulation can support emotional processing.
                  </p>
                </div>
              </div>
              <div className="w-full md:w-1/3 px-4 mb-8">
                <div className="p-6 bg-white rounded-lg border border-tint">
                  <img
                    src="/images/quick-results.webp"
                    alt="Accessible Approach"
                    className="mx-auto mb-4 w-16 h-16"
                  />
                  <h3 className="text-xl font-semibold mb-2 text-text">Accessible Approach</h3>
                  <p className="text-base text-textSecondary">
                    Self-directed sessions can be used alongside professional support.
                  </p>
                </div>
              </div>
              <div className="w-full md:w-1/3 px-4 mb-8">
                <div className="p-6 bg-white rounded-lg border border-tint">
                  <img
                    src="/images/non-invasive.webp"
                    alt="Safe and Gentle"
                    className="mx-auto mb-4 w-16 h-16"
                  />
                  <h3 className="text-xl font-semibold mb-2 text-text">Safe and Gentle</h3>
                  <p className="text-base text-textSecondary">
                    Non-invasive techniques designed for personal wellness support.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="surfaceTint" />

        {/* Scientific Recognition */}
        <section className="pt-8 pb-12 bg-surfaceTint">
          <div className="max-w-screen-md mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4 text-text">
              Research Recognition
            </h2>
            <p className="text-base mb-4 text-textSecondary">
              Bilateral stimulation techniques have been studied and recognized by
              various organizations, including:
            </p>
            <ul className="list-disc list-inside text-base mb-4 text-textSecondary">
              <li>World Health Organization (WHO)</li>
              <li>American Psychiatric Association (APA)</li>
              <li>Department of Veterans Affairs (VA)</li>
            </ul>
            <p className="text-base text-textSecondary">
              While our sessions are inspired by this research, they are not therapy
              and should not replace professional mental health care.
            </p>
          </div>
        </section>

        <WaveDivider topColor="surfaceTint" bottomColor="background" />

        {/* Research & Studies */}
        <section className="pt-8 pb-12 bg-background">
          <div className="max-w-screen-xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-2 text-center text-text">
              Research & Studies
            </h2>
            <p className="text-base text-textSecondary text-center mb-8 max-w-2xl mx-auto">
              <strong>Important:</strong> These studies examine professional therapy applications.
              Our sessions are inspired by this research but are not therapy.
            </p>

            <div className="bg-white p-6 rounded-lg border border-tint mb-6">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">40-91%</div>
                  <p className="text-sm text-textSecondary">PTSD remission rates in clinical trials</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">35+</div>
                  <p className="text-sm text-textSecondary">Randomized controlled trials</p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">3-6</div>
                  <p className="text-sm text-textSecondary">Sessions for single trauma cases</p>
                </div>
              </div>
            </div>

            <div className="max-w-3xl mx-auto">
              <Accordion title="Key Research Findings" defaultOpen={true}>
                <div className="space-y-3 text-textSecondary">
                  <div className="flex items-start">
                    <div className="text-accent font-bold mr-2">•</div>
                    <p className="text-sm">VA/DoD gives bilateral stimulation techniques their highest <a href="https://www.ptsd.va.gov/professional/treat/txessentials/emdr_pro.asp" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">"Strong" recommendation</a></p>
                  </div>
                  <div className="flex items-start">
                    <div className="text-accent font-bold mr-2">•</div>
                    <p className="text-sm">Large effect sizes for depression treatment (Hedges' g = 0.75) - <a href="https://www.mdpi.com/2077-0383/13/18/5633" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">2024 Meta-Analysis</a></p>
                  </div>
                  <div className="flex items-start">
                    <div className="text-accent font-bold mr-2">•</div>
                    <p className="text-sm">Neuroscience studies show amygdala suppression during bilateral stimulation - <a href="https://connect.springerpub.com/highwire_display/entity_view/node/69871/full" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Neuroimaging Research</a></p>
                  </div>
                </div>
              </Accordion>

              <Accordion title="Government & Official Sources">
                <ul className="text-sm text-textSecondary space-y-2">
                  <li>• <a href="https://www.ptsd.va.gov/professional/treat/txessentials/emdr_pro.asp" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">VA/DoD PTSD Treatment Guidelines</a></li>
                  <li>• <a href="https://www.who.int/news/item/06-08-2013-who-releases-guidance-on-mental-health-care-after-trauma" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">WHO Mental Health Guidelines</a></li>
                  <li>• <a href="https://www.nice.org.uk/guidance/ng116/chapter/recommendations" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">UK NICE Treatment Guidelines</a></li>
                </ul>
              </Accordion>

              <Accordion title="Meta-Analyses & Systematic Reviews">
                <ul className="text-sm text-textSecondary space-y-2">
                  <li>• <a href="https://www.mdpi.com/2077-0383/13/18/5633" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">2024 Depression Meta-Analysis (MDPI)</a></li>
                  <li>• <a href="https://pubmed.ncbi.nlm.nih.gov/37882423/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">2023 PTSD Systematic Review</a></li>
                  <li>• <a href="https://pubmed.ncbi.nlm.nih.gov/32043428/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">2020 Mental Health Meta-Analysis</a></li>
                  <li>• <a href="https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0103676" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">2014 Efficacy Meta-Analysis (PLOS One)</a></li>
                </ul>
              </Accordion>

              <Accordion title="Neuroscience Research">
                <ul className="text-sm text-textSecondary space-y-2">
                  <li>• <a href="https://bmcpsychology.biomedcentral.com/articles/10.1186/s40359-022-00951-0" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Working Memory Taxation Studies</a></li>
                  <li>• <a href="https://connect.springerpub.com/highwire_display/entity_view/node/69871/full" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Neuroimaging Correlates Research</a></li>
                  <li>• <a href="https://www.sciencedirect.com/science/article/abs/pii/S2468749920300788" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Bilateral Stimulation Effects Studies</a></li>
                </ul>
              </Accordion>
            </div>

            <p className="text-center text-xs text-textSecondary italic mt-6 max-w-2xl mx-auto">
              Our self-directed sessions are inspired by this research but are not therapy
              and cannot replace professional mental health care.
            </p>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="background" />

        {/* Interactive Demo Section */}
        <section className="pt-8 pb-12 bg-background">
          <div className="max-w-screen-md mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4 text-text">
              Try Our Interactive Demo
            </h2>
            <p className="text-lg text-textSecondary mb-6">
              Experience how our sessions work with our interactive tour.
              No signup required - try it now to see what makes our approach unique.
            </p>
            <div className="bg-surfaceTint border-l-4 border-secondary p-4 mb-6 rounded text-left">
              <p className="text-text text-sm font-medium mb-2">
                🎯 <strong>What you'll experience:</strong>
              </p>
              <ul className="text-textSecondary text-sm space-y-1">
                <li>• Interactive mood tracking demonstration</li>
                <li>• Customizable session settings (colors, speed, size)</li>
                <li>• Bilateral stimulation session preview</li>
                <li>• Complete walkthrough of our process</li>
              </ul>
            </div>
            <Link
              to="/demo"
              state={{ from: location.pathname }}
              className="inline-block bg-accent text-white font-semibold py-3 px-8 rounded-full hover:bg-accentDark transition-colors text-lg"
            >
              🚀 Try Interactive Demo
            </Link>
            <p className="text-sm text-textSecondary mt-4">
              Takes about 2-3 minutes • No account needed
            </p>
          </div>
        </section>

        <WaveDivider topColor="background" bottomColor="primary" />

        {/* Call to Action */}
        <section className="bg-primary text-white pt-8 pb-12">
          <div className="max-w-screen-md mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4">
              Start Your Wellness Journey
            </h2>
            <p className="text-base mb-8">
              Explore our research-inspired sessions designed to support your mental wellness.
            </p>
            <Link
              to="/subscribe"
              className="inline-block bg-white text-primary font-semibold py-3 px-6 rounded-full hover:bg-surfaceTint transition-colors"
            >
              Subscribe Now
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default AboutEMDR;
